import {
  getControllerCandidateResumeSubmission
} from '../../../components/Candidates/ResumeSubmission/CandidateResumeSubmission';
import {
  CANDIDATE_SUMMARY__INCLUDE_FLAGS
} from '../../../components/Candidates/SubmissionNotes.lib';
import Core from "../../Core";
import {
  EMPLOYER_SUBMISSION_METHOD__ATS,
  ID__ATS_TYPE__ASHBY_API,
  ID__ATS_TYPE__GH_HARVEST,
  ID__ATS_TYPE__GH_INGESTION,
  ID__ATS_TYPE__LEVER_API
} from "../../Definition";
import {
  tools
} from "../../GenericTools.lib";
import Google from "../../Google";
import {
  Obj
} from '../../Object.lib';
import {
  COLLECTION__JOBS
} from '../BE/loopback.api';
import Ashby from './Ashby.lib';
import getAtsContextProperty from './getAtsContextProperty.fun';
import getAtsContextSingle from './getAtsContextSingle.fun';
import Greenhouse from "./Greenhouse.lib";
import Lever from './Lever.lib';

/* METHODS ==================================== */

/**
 * Handler event on click "Submit to ATS" green button
 *
 * @param {string} apiName "harvest" | "ingestion"
 * @returns A promise.
 */
async function sendToATS({
  apiName = '',
  candidate = {},
  job = {},
  submissionNotes = {},
  emailParams = {},
}) {

  const employer = Obj(job.employer);
  const atsTypeId = parseInt(employer.atsTypeId);

  if (atsTypeId === ID__ATS_TYPE__LEVER_API) {
    return Lever.submit({ candidate, job, emailParams });
  }

  if (atsTypeId === ID__ATS_TYPE__ASHBY_API) {
    return Ashby.submit({
      candidate,
      job,
      emailParams,
      submissionNotes: submissionNotes.public,
    });
  }

  const atsJobId = getAtsContextProperty({
    collection: COLLECTION__JOBS,
    entity: job,
    atsTypeId,
    property: 'jobId'
  });

  const CONTEXT__ATS__EMPLOYER = getAtsContextSingle({
    entity: employer,
    atsTypeId
  });

  const {
    /** common for greenhouse */
    apiKey: atsApiKey,
    /** specific for greenhouse harvest */
    onBehalfOfEmployerUserId: atsUserId,
    sourceIdAgencyId: atsSourceId,
    agencyRecruiterName: atsReferrer,
    /** specific for greenhouse ingestion */
    onBehalfOfEmployerUser,
  } = CONTEXT__ATS__EMPLOYER;

  const atsApiName = tools.capitalize(apiName);

  const isAtsGhHarvestSelected = (
    atsTypeId === ID__ATS_TYPE__GH_HARVEST
  );

  const isAtsGhIngestionSelected = (
    atsTypeId === ID__ATS_TYPE__GH_INGESTION
  );

  const atsOnBehalfOf = (
    isAtsGhHarvestSelected
      ? atsUserId
      : isAtsGhIngestionSelected
        ? onBehalfOfEmployerUser
        : null
  );

  /** MAKING CALL TO GREENHOUSE */
  return await Greenhouse[apiName].addCandidate({

    /** PARAMS TO SEND */
    candidate,
    atsApiKey,
    atsOnBehalfOf,
    atsSourceId,
    atsUserId,
    atsJobId,
    atsReferrer,
    submissionNotes,

    /** SUCCESS CALLBACK */
    onSuccess: async response => {

      const {
        id: greenhouseAtsApplicationId
      } = response;

      Core.dialog.open({
        title: "Success",
        message: "Added candidate to greenhouse successfully!",
        paperStyle: { width: 640 }
      });

      /** µ FOR DEBUG PURPOSES * /
      console.debug('µ:CandoSubmitResume::onSuccess', response);
      /** */

      /* SEND EMAIL TO ADMIN ============== */

      const emailOptions = {
        ...emailParams,
        to: Core.getResumeSubmissionBcc()
      };

      emailOptions.subject = (
        `Submitted via Greenhouse ${atsApiName} API | ${emailOptions.subject}`
      );

      await Google.sendEmail(emailOptions);

      /* ============== SEND EMAIL TO ADMIN */

      /** MAKE STAGE TRANSITION */
      await getControllerCandidateResumeSubmission().transitionAfterSubmission({
        submissionMethodId: EMPLOYER_SUBMISSION_METHOD__ATS,
        greenhouseAtsApplicationId
      });
      /** */

    },

    /** FAILURE CALLBACK */
    onFailure: (failures, suggestions, statusCode) => {

      const dialogTitle = (
        statusCode
          ? `Greenhouse ${atsApiName} API - POST Candidate failed`
          : `Some values are bad formatted`
      );

      Core.dialog.open({
        title: dialogTitle,
        message: (
          <>
            {(
              statusCode
                ? <h4>Error code: {statusCode}</h4>
                : ''
            )}
            <h4>Error messages:</h4>
            <small>
              <b>
                <pre>{failures.join('\n\n')}</pre>
              </b>
            </small>
            <h4>Suggestions for taking action:</h4>
            <small>
              <b>
                <i>
                  <pre>{suggestions.join('\n')}</pre>
                </i>
              </b>
            </small>
          </>
        )
      });

      /* SEND EMAIL TO ADMIN ============== */

      const emailOptions = {
        ...emailParams,
        to: Core.getResumeSubmissionBcc()
      };

      emailOptions.subject = (
        `ERROR | Greenhouse ${atsApiName} API failed | ${emailOptions.subject}`
      );

      /* adding failures to the email body */
      emailOptions.html = emailOptions.html.replace('<html>', '');
      emailOptions.html = emailOptions.html.replace('<head></head>', '');
      emailOptions.html = emailOptions.html.replace('<body>', '');
      emailOptions.html = (
        (`
          <html>
            <head></head>
            <body>
              <p>
                --------------------------------------------------
              </p>
              <p>
                Failures:
              </p>
              <p>
                <ul>
                  <li>
                    ${failures.join('</li><li>')}
                  </li>
                </ul>
              </p>
              <p>
                Suggestions:
              </p>
              <p>
                <ul>
                  <li>
                    ${suggestions.join('</li><li>')}
                  </li>
                </ul>
              </p>
              <p>
                --------------------------------------------------
              </p>
        `).replace(/\s{10}/g, '') +
        emailOptions.html
      );

      Google.sendEmail(emailOptions);

      /* ============== SEND EMAIL TO ADMIN */

    }
  });

}

/**
 * Getting public and private notes
 * from raw submission notes
 *
 * @param {*} param0
 * @returns
 */
function makeSubmissionNotes({
  candidate = {},
  rawSubmissionNotes
}) {

  const submissionNotes = {
    public: [],
    private: []
  };

  rawSubmissionNotes
    .split('\n')
    .map(
      // Fixes spacing in key: value pairs,
      // preserving valid URLs
      (line) => line.replace(
        /(^|\s)([^:\s]+):\s*(?!\/\/)/g,
        '$1$2: '
      )
    )
    .filter(n => {
      if (
        !n ||
        !!n.match(/CANDIDATE SUBMISSION:/i) ||
        !!n.match(/salary/i)
      ) {
        return false;
      }
      if (
        !!n.match(/visa/i) ||
        !!n.match(/work authorization/i)
      ) {
        submissionNotes.private.push(n);
      }
      else {
        submissionNotes.public.push(n);
      }
      return true;
    })
    .join('\n');

  if (candidate.submissionIncludeFlags.includes(CANDIDATE_SUMMARY__INCLUDE_FLAGS.minimumSalary)) {
    submissionNotes.private.push([
      'Compensation expectation: ',
      candidate._minimumSalary,
      candidate.salaryNote ? ' - ' + candidate.salaryNote : ''
    ].join(''));
  }

  submissionNotes.public = submissionNotes.public.join('\n');
  submissionNotes.private = submissionNotes.private.join('\n');

  /** µ FOR DEBUG PURPOSES * /
  console.debug('µ', {
    rawSubmissionNotes,
    submissionNotes,
  });
  /** */

  return submissionNotes;

}

/* DICTIONARIES =============================== */

const AtsLib = {
  send: sendToATS,
  makeSubmissionNotes,
}

/* EXPORTS ==================================== */

export {
  AtsLib, AtsLib as default, makeSubmissionNotes as makeAtsSubmissionNotes, sendToATS
};

/* ============================================ */
